import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useSessionContext } from "@/contexts/SessionContext";
import { useTaskWatchingContext } from "@/contexts/TaskWatchingContext";
import { useFormatter } from "@/utils/useFormatter";
import { oids } from "@/configs/Settings";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import type { TeleconsultoriaGuest } from "../../types";
import { readWherebyRoom } from "../../utils/virtualService";
import { inviteErrorMessage } from "../helpers/inviteErrorMessage";
import { inviteAppointmentId, isInviteResponseTask } from "../helpers/inviteTaskHelpers";
import { practitionerRoleReference } from "../helpers/practitionerRoleReferences";
import { InviteActionError } from "../services/inviteActionError";
import { findPendingInviteTasks } from "../services/inviteTaskService";
import { cancelTeleconsultoriaInvite } from "../services/respondTeleconsultoriaInvite";
import {
  sendTeleconsultoriaInvite,
  type SendInviteResult,
} from "../services/sendTeleconsultoriaInvite";

/** PractitionerRole e nome do profissional logado (o host do convite). */
export function useHostIdentity() {
  const { access, user } = useSessionContext();
  return {
    hostRoleReference: practitionerRoleReference({
      organizationId: access?.organizationId || "",
      practitionerReference: `Practitioner/${oids.cpf}-${user?.username}`,
    }),
    hostDisplay: useFormatter.formatNameDisplay(user?.name || ""),
  };
}

/**
 * Lado do HOST no atendimento: enviar, acompanhar e cancelar o convite.
 * Ao abrir a tela, procura convite pendente do Appointment para restaurar o estado
 * (e a sala de vídeo do host) mesmo depois de recarregar a página.
 */
export function useHostTeleconsultoriaInvite(params: {
  appointment?: fhir5.Appointment;
  onRoomReady: (roomUrl: string) => void;
}) {
  const { appointment, onRoomReady } = params;
  const { hostRoleReference, hostDisplay } = useHostIdentity();
  const [pendingInvite, setPendingInvite] = useState<fhir5.Task | null>(null);

  const appointmentId = appointment?.id;
  const roomUrl = readWherebyRoom(appointment?.virtualService)?.addressUrl;
  useEffect(() => {
    if (!appointmentId) return;
    let isCancelled = false;
    void findPendingInviteTasks(appointmentId).then((tasks) => {
      if (isCancelled || !tasks[0]) return;
      setPendingInvite(tasks[0]);
      if (roomUrl) onRoomReady(roomUrl);
    });
    return () => {
      isCancelled = true;
    };
    // onRoomReady é setState do pai; só reage à troca de atendimento.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  // Resposta do convidado chegou (notificação via SSE): o convite deixa de estar
  // pendente aqui, sem precisar recarregar a tela.
  const { allTasks } = useTaskWatchingContext();
  const hasResponseForAppointment = useMemo(
    () =>
      Boolean(appointmentId) &&
      (Array.isArray(allTasks) ? allTasks : []).some(
        (task) => isInviteResponseTask(task) && inviteAppointmentId(task) === appointmentId,
      ),
    [allTasks, appointmentId],
  );
  useEffect(() => {
    if (hasResponseForAppointment) setPendingInvite(null);
  }, [hasResponseForAppointment]);

  const sendTask = useAsyncTask({
    task: useCallback(
      (guest: TeleconsultoriaGuest): Promise<SendInviteResult> => {
        if (!appointment) throw new Error("Atendimento ausente");
        return sendTeleconsultoriaInvite({ appointment, guest, hostRoleReference, hostDisplay });
      },
      [appointment, hostRoleReference, hostDisplay],
    ),
  });
  const cancelTask = useAsyncTask({ task: cancelTeleconsultoriaInvite });

  const { run: runSend } = sendTask;
  const send = useCallback(
    async (guest: TeleconsultoriaGuest): Promise<boolean> => {
      try {
        const result = await runSend(guest);
        setPendingInvite(result.task);
        onRoomReady(result.roomUrl);
        toast.success(`Convite enviado para ${guest.practitioner.display || "o teleconsultor"}.`);
        return true;
      } catch (error) {
        console.error("useHostTeleconsultoriaInvite.send", error);
        toast.warning(inviteErrorMessage(error, "Não foi possível enviar o convite."));
        return false;
      }
    },
    [runSend, onRoomReady],
  );

  const { run: runCancel } = cancelTask;
  const cancel = useCallback(async () => {
    if (!pendingInvite) return;
    try {
      await runCancel(pendingInvite);
      setPendingInvite(null);
      toast.info("Convite cancelado.");
    } catch (error) {
      console.error("useHostTeleconsultoriaInvite.cancel", error);
      // Já respondido (aceito/recusado): o convite deixa de ser "pendente" na tela.
      if (error instanceof InviteActionError) setPendingInvite(null);
      toast.error(inviteErrorMessage(error, "Não foi possível cancelar o convite."));
    }
  }, [pendingInvite, runCancel]);

  return {
    pendingInvite,
    send,
    cancel,
    isSending: sendTask.loading,
    isCancelling: cancelTask.loading,
  };
}
